'use client'
import React, { useState, useEffect } from "react";
import { Link, useLocation } from 'react-router-dom'; // Use react-router-dom for routing.
import eventsData from '../../data/eventsData.json';
import {
  fetchPublicEvents,
  getPublicEventCtaLabel,
  getPublicEventPath,
  mapApiEventToCard,
} from '../../services/publicEventsApi';

const toTime = (value) => {
  const t = value ? new Date(value).getTime() : NaN;
  return Number.isNaN(t) ? null : t;
};

// À venir d'abord (le plus proche en premier), puis les passés (le plus récent en premier).
const sortByEventDate = (cards) => {
  const now = Date.now();
  return [...cards].sort((a, b) => {
    const startA = toTime(a._raw?.start_date);
    const startB = toTime(b._raw?.start_date);
    if (startA === null && startB === null) return 0;
    if (startA === null) return 1;
    if (startB === null) return -1;

    const endA = toTime(a._raw?.end_date) ?? startA;
    const endB = toTime(b._raw?.end_date) ?? startB;
    const pastA = endA < now;
    const pastB = endB < now;
    if (pastA !== pastB) return pastA ? 1 : -1;

    return pastA ? startB - startA : startA - startB;
  });
};

const EventMain = () => {
    const location = useLocation();
    const [activeTab, setActiveTab] = useState("1st-day");
    const [categories, setCategories] = useState([]);
    const [events, setEvents] = useState([]);
    const [loadError, setLoadError] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
      const defs = eventsData?.categories || [];
      setCategories(defs);

      let cancelled = false;
      (async () => {
        setLoading(true);
        setLoadError(null);
        try {
          const { events: apiList } = await fetchPublicEvents({ limit: 500 });
          if (cancelled) return;
          const cards = apiList.map((e) => mapApiEventToCard(e, defs));
          setEvents(sortByEventDate(cards));
        } catch (e) {
          if (!cancelled) {
            setLoadError(e?.message || "Impossible de charger les événements.");
            setEvents([]);
          }
        } finally {
          if (!cancelled) setLoading(false);
        }
      })();

      return () => {
        cancelled = true;
      };
    }, []);

    const handleTabClick = (tab) => {
      setActiveTab(tab);
    };

    const searchTerm = new URLSearchParams(location.search).get("q")?.trim().toLowerCase() || "";

    // « Tout » : afficher tous les événements publiés
    const getEventsByCategory = (categoryId) => {
      let list = categoryId === "1st-day"
        ? events
        : events.filter((event) => event.category === categoryId);

      if (searchTerm) {
        list = list.filter((event) => {
          const title = String(event.title || "").toLowerCase();
          const desc = String(event.description || "").toLowerCase();
          const place = String(event.location || "").toLowerCase();
          return (
            title.includes(searchTerm) ||
            desc.includes(searchTerm) ||
            place.includes(searchTerm)
          );
        });
      }
      return list;
    };

    // Map image names to actual imported images
    // const getImagePath = (imageName) => {
    //   const imagePaths = {
    //     '309X166.jpg': EventImg1,
    //     '309X166_2.jpg': EventImg2,
    //     'schedule-one-1-4.jpg': EventImg3,
    //     'schedule-one-1-5.jpg': EventImg4,
    //     'schedule-one-1-6.jpg': EventImg5,
    //     'schedule-one-1-7.jpg': EventImg6,
    //     'schedule-one-1-8.jpg': EventImg7,
    //     'schedule-one-1-9.jpg': EventImg8,
    //   };
    //   return imagePaths[imageName] || EventImg1;
    // };
    return (
        <React.Fragment>
            {/* Service Details Start */}
            <section className="body-bg-color-2 event-page">
            <div className="container">
              <div className="schedule-one__inner">
                <div className="section-title text-left">
                  <div className="section-title__tagline-box">
                    <span className="section-title__tagline">
                      événements
                    </span>
                  </div>
                  <h2 className="section-title__title section-title__title--two">
                    Tous vos évènements
                  </h2>
                </div>
                <div className="schedule-two__main-tab-box tabs-box">
                  {loadError && (
                    <p className="text-danger mb-3" role="alert">
                      {loadError}
                    </p>
                  )}
                  {loading && !loadError && (
                    <p className="mb-3">Chargement des événements…</p>
                  )}
                  <ul className="tab-buttons clearfix list-unstyled">
                    {categories.map((category) => (
                      <li
                        key={category.id}
                        className={`tab-btn ${
                          activeTab === category.id ? "active-btn" : ""
                        }`}
                        onClick={() => handleTabClick(category.id)}
                      >
                        <h3>{category.displayName}</h3>
                      </li>
                    ))}
                  </ul>
                  <div className="tabs-content">
                    {categories.map((category) => (
                      <div
                        key={category.id}
                        className={`tab ${
                          activeTab === category.id ? "active-tab" : ""
                        }`}
                        id={category.id}
                      >
                        <div className="schedule-one__tab-content-box">
                          {getEventsByCategory(category.id).map((event) => (
                            <div key={event.id} className="schedule-one__single">
                              <div className="schedule-one__left">
                                <h3 className="schedule-two__title">
                                  <Link to={getPublicEventPath(event)}>
                                    {event.title}
                                  </Link>
                                </h3>
                                <p className="schedule-two__text">
                                  {event.description}
                                </p>
                              </div>
                              <div className="schedule-one__img">
                                <img src={(event.image)} alt={event.title} />
                              </div>
                              <div className="schedule-one__address-and-btn-box">
                                <ul className="list-unstyled schedule-two__address">
                                  <li>
                                    <div className="icon">
                                      <span className="icon-clock"></span>
                                    </div>
                                    <div className="text">
                                      <p>
                                        {event.time} <br /> {event.date}
                                      </p>
                                    </div>
                                  </li>
                                  <li>
                                    <div className="icon">
                                      <span className="icon-pin"></span>
                                    </div>
                                    <div className="text">
                                      <p>
                                        {event.location}
                                      </p>
                                    </div>
                                  </li>
                                </ul>
                                <div className="schedule-one__btn-box">
                                  <Link to={getPublicEventPath(event)} className="schedule-one__btn thm-btn">
                                    {event.ctaLabel || getPublicEventCtaLabel(event)}
                                  </Link>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </section>
            {/* Service Details End */}
        </React.Fragment>
    );
};

export default EventMain;
